import React, { useEffect, useState, useRef } from 'react';

import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router';

import queryString from 'query-string';
import { find, get, isEmpty, isNil, orderBy, cloneDeep, startCase } from 'lodash';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import * as math from 'mathjs';
import clsx from 'clsx';

import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputAdornment from '@material-ui/core/InputAdornment';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import AutoComplete from '../../../components/FormFields/Autocomplete';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import CustomCalculationPanel from './CustomCalculationPanel';
import BobConfirmButton from '../../../components/BobConfirmButton';
import BobCancelButton from '../../../components/BobCancelButton';
import BobLoadingMask from '../../../components/BobLoadingMask';
import CreateSensorWarningModal from '../Modals/CreateSensorWarningModal';

import * as ArchitectActions from '../../../actions/architectActions';
import * as BasAgentActions from '../../../actions/basAgentActions';
import * as BobActions from '../../../actions/bobActions';
import * as CoreActions from '../../../actions/coreActions';
import * as DashboardActions from '../../../actions/dashboardActions';
import * as DeviceActions from '../../../actions/deviceActions';
import * as SensorTypeActions from '../../../actions/sensorTypeActions';

import { enqueueSnackbar } from '../../../actions/applicationMessageActions';

import * as classes from './SensorForm.module.css';

const SensorValidationSchema = Yup.object().shape({
    client: Yup.string()
        .required(),
    site: Yup.string()
        .required(),
    parent: Yup.string(),
    name:   Yup.string()
        .required(),
    sensorType: Yup.string()
        .required(),
    unit: Yup.string()
        .nullable()
        .when('dataType', {
            is:        'float',
            then:      Yup.string().required(),
            otherwise: Yup.string().notRequired()
        }),
    displayUnit: Yup.string()
        .nullable()
        .when('displayDataType', {
            is:        'float',
            then:      Yup.string().required(),
            otherwise: Yup.string().notRequired()
        }),
    useCalculation: Yup.boolean()
        .required(),

    agent: Yup.string()
        .when('useCalc', {
            is:        false,
            then:      Yup.string().required(),
            otherwise: Yup.string().notRequired()
        }),
    device: Yup.string()
        .nullable()
        .when('useCalc', {
            is:        false,
            then:      Yup.string().required(),
            otherwise: Yup.string().notRequired()
        }),
    deviceObject: Yup.string()
        .when('useCalc', {
            is:        false,
            then:      Yup.string().required(),
            otherwise: Yup.string().notRequired()
        }),

    dataType: Yup.string()
        .required(),
    displayDataType: Yup.string()
        .required(),
    conversion: Yup.string()
        .nullable()
});

const dataTypes = ['boolean', 'float', 'integer', 'text'];

const SensorForm = ({
    activeNavClient,
    activeNavEquipment,
    activeNavSite,
    agents,
    bobLoading,
    conversions,
    companyInfo,
    devices,
    entitySensorData,
    fetchBasAgents,
    fetchBasAgent,
    fetchAgentById,
    fetchConversions,
    fetchDevicesList,
    fetchDeviceObjects,
    fetchEntitySensorData,
    fetchSensorBySensorId,
    fetchSensorTypes,
    fetchTemplateSensorById,
    fetchRawUnits,
    fetchSystemsBySiteId,
    deviceObjects,
    history,
    navClients,
    navEquipment,
    navSites,
    selectedSensor,
    sensorCalculations,
    sensorHistory,
    sensorTypes,
    sensors,
    setBobLoading,
    setProgressBarLoading,
    setSelectedSensor,
    units,
    ...props
}) => {
    const dispatch = useDispatch();
    const extEq = useRef(null);
    const [useCalc, setUseCalc] = useState(false);
    const [displayUnitTypeDisabled, setDisplayUnitTypeDisabled] = useState(false);
    const [displayUnits, setDisplayUnits] = useState([]);
    const [unitTypeDisabled, setUnitTypeDisabled] = useState(false);
    const [calculationId, setCalculationId] = useState('');
    const [filteredConversions, setFilteredConversions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [loadingDeviceObjects, setLoadingDeviceObjects] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [sensorSelection, setSensorSelection] = useState(['']);
    const [systems, setSystems] = useState([]);
    const [selectedCalculation, setSelectedCalculation] = useState({});
    const [distinctAgentList, setDistinctAgentList] = useState([]);
    const [distinctDeviceList, setDistinctDeviceList] = useState([]);
    const [activeAgent, setActiveAgent] = useState({});
    const [warningModalOpen, setWarningModalOpen] = useState(false);
    const [warningFunction, setWarningFunction] = useState(false);
    const [startValue, setStartValue] = useState(0);
    const [filteredAgentList, setFilteredAgentList] = useState([]);
    const [isValidFormula, setIsValidFormula] = useState(false);
    const [search, setSearch] = useState('');
    const [calculatedVal, setCalculatedVal] = useState('');
    const [externalSensors, setExternalSensors] = useState([]);

    const queryValues = queryString.parse(history.location.search);
    const companyId = queryValues.company;
    const siteId = queryValues.site;
    const equipmentId = queryValues.equipment;
    const systemId = queryValues.system;
    const sensorId = queryValues.sensor;

    const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

    const [initialValues, setInitialValues] = useState({
        client:          activeNavClient.id || '',
        site:            activeNavSite.id || '',
        parent:          activeNavEquipment.id || '',
        name:            '',
        sensorType:      '',
        unit:            '',
        displayUnit:     '',
        agent:           '',
        device:          '',
        deviceObject:    '',
        dataType:        '',
        displayDataType: '',
        conversion:      '',
        useCalculation:  false
    });

    useEffect(() => {
        return () => {
            setSelectedSensor({});
            const { pathname } = history.location;
            const params = new URLSearchParams(history.location.search);
            let entityId = '';
            let sensors = [];
            let rule = {};

            if (sensorHistory) {
                sensors = sensorHistory.sensorHistory;
                rule = sensorHistory.ruleHistory;
                entityId = sensorHistory.entityId;
            }
            const updatedUrl = history.location.search.replace(/[?&]sensor=[^&]+/, '').replace(/^&/, '?');
            params.delete('sensor');

            history.push({ pathname, search: updatedUrl, state: { entityId: entityId, sensorHistory: [...sensors], ruleHistory: rule }});
        };
    }, []);

    useEffect(() => {
        if (entitySensorData && entitySensorData.length > 0) {
            let _sensors = getSensorIds();
            for (let sid of _sensors) {
                if (activeNavEquipment.extendedData && activeNavEquipment.extendedData.sensors && activeNavEquipment.extendedData.sensors.length > 0) {
                    let ts = activeNavEquipment.extendedData.sensors.filter(c => c.id === sid[0]);
                    if (isEmpty(ts)) {
                        ts = externalSensors.filter(c => c.id === sid[0]);
                    }
                    if (ts && ts.length > 0) {
                        let sensorId = ts[0].id;
                        for (let ds of entitySensorData) {
                            if (sensorId === ds.sensorId) {
                                if (ds && ds.data && ds.data.length > 0) {
                                    let cval = ds.data;
                                    if (cval && cval.length > 0) {
                                        _sensors.set(sid[0], cval[0].convertedValue);
                                    }
                                }
                            }
                        }
                    }
                }
            }

            try {
                let formula = '';
                let input = document.querySelector('#formula-editor');
                if (input && input.childNodes) {
                    for (let i = 0; i < input.childNodes.length; i++) {
                        if (input.childNodes[i].getAttribute && input.childNodes[i].getAttribute('data-ui-variable')) {
                            if (input.childNodes[i].classList.contains('variable-token')) {
                                formula += _sensors.get(input.childNodes[i].getAttribute('data-ui-variable'));
                            }
                            else {
                                setIsValidFormula(false);
                                setCalculatedVal('Expression is not valid.');
                                throw new Error('Expression is not valid.');
                            }
                        }
                        else if (input.childNodes[i].tagName && input.childNodes[i].tagName.toLowerCase() === 'input') {
                            formula += input.childNodes[i].value;
                        }
                        else formula += input.childNodes[i].textContent;
                    }
                    formula = formula.replace(/(\r\n|\n|\r)/gm, '').trim();
                    formula = formula.replace(/\u00A0/g, '');
                    let tempFormula = formula.replaceAll('avg', 'mean');
                    tempFormula = tempFormula.replaceAll('Math.', '');
                    tempFormula = tempFormula.replaceAll('{', '').replaceAll('}', '');
                    try {
                        let hasNull = false;
                        let hasPollFailure = false;

                        if (tempFormula.includes(null) || tempFormula.includes('null')) hasNull = true;
                        if (tempFormula.includes('Poll Failure')) hasPollFailure = true;

                        let calculatedformula = tempFormula.replaceAll(null, '0');
                        calculatedformula = calculatedformula.replaceAll('null', '0');
                        calculatedformula = calculatedformula.replaceAll('Poll Failure', '0');

                        calculatedformula = calculatedformula.replaceAll('runtime_calculation', '');
                        let result = math.evaluate(calculatedformula);
                        setCalculatedVal(hasNull ? formula + ' = null' : formula + '=' + result);
                        setIsValidFormula(true);

                        if (hasNull) {
                            dispatch(enqueueSnackbar('One or more sensors does not contain sensor data.', 'warning'));
                        } else if (hasPollFailure) {
                            dispatch(enqueueSnackbar('One or more sensors has a Poll Failure.', 'warning'));
                        } else {
                            dispatch(enqueueSnackbar('Calculation tested successfully.', 'success'));
                        }
                    } catch (e) {
                        setCalculatedVal('Expression is not valid.');
                        setIsValidFormula(false);
                        dispatch(enqueueSnackbar('Expression is not valid.', 'error'));
                    }
                }
            }
            catch (e) {
                setIsValidFormula(false);
                setCalculatedVal('Expression is not valid.');
                dispatch(enqueueSnackbar('Expression is not valid.', 'error'));
            }
        }

    }, [entitySensorData]);

    useEffect(() => {
        const init = async () => {
            const distinctAgents = [...new Set(sensors.map(sensor => sensor.agentId))];
            const distinctDevices = [...new Set(sensors.map(sensor => sensor.deviceId))];
            setDistinctAgentList(distinctAgents.filter(s => s !== EMPTY_GUID));
            setDistinctDeviceList(distinctDevices.filter(s => s !== EMPTY_GUID));
            if (!sensorId) {
                await setDefaultAgentAndDevice();
            }
        };
        if (sensors.length > 0) init();
    }, [sensors]);

    useEffect(() => {
        const init = async () => {
            if (distinctDeviceList.length === 1 && distinctDeviceList[0] !== EMPTY_GUID) {
                const distinctDeviceId = distinctDeviceList[0];
                const foundDevice = devices.find(device => device.id === distinctDeviceId);
                if (foundDevice) {
                    formik.setFieldValue('device', distinctDeviceId, false);
                    await fetchDeviceObjects(distinctDeviceId);
                }
            }
        };
        if (devices && !sensorId) init();
    }, [devices]);

    useEffect(() => {
        const init = async () => {
            if (sensorId) {
                if (isEmpty(selectedSensor)) {
                    setProgressBarLoading(true);
                    await fetchSensorBySensorId(sensorId);
                    setProgressBarLoading(false);
                }
            }

            await Promise.all([
                fetchConversions(companyId),
                fetchSensorTypes(),
                fetchBasAgents(companyId)
            ].map(p => p.catch(err => err)));
        };

        if (companyInfo.companyId) init();
    }, [companyInfo.companyId]);

    useEffect(() => {
        if (filteredAgentList.length === 0) {
            populateFilteredAgentList();
        }
    }, [agents]);

    useEffect(() => {
        populateFilteredAgentList();

        const init = async () => {
            setProgressBarLoading(true);
            const fetchedSystems = await fetchSystemsBySiteId(siteId);
            setSystems(fetchedSystems);
            setInitialValues({
                ...initialValues,
                client: get(activeNavClient, 'id', ''),
                site:   get(activeNavSite, 'id', ''),
                parent: systemId || ''
            });

            if (sensorId) {
                const selectedAgent = await fetchAgentById(get(selectedSensor, 'agentId', ''));
                setActiveAgent(selectedAgent);
                const configurationId = get(selectedAgent, 'driverConfiguration[0].id', '');
                await fetchDevicesList(get(selectedSensor, 'agentId', ''), configurationId);
                await fetchDeviceObjects(get(selectedSensor, 'deviceId', ''));

                formik.setFieldValue('name', get(selectedSensor, 'name', ''), false);
                formik.setFieldValue('sensorType', get(selectedSensor, 'sensorType', ''), false);
                formik.setFieldValue('agent', get(selectedSensor, 'agentId', ''), false);
                formik.setFieldValue('device', get(selectedSensor, 'deviceId', ''), false);
                formik.setFieldValue('deviceObject', get(selectedSensor, 'deviceObjectId', ''), false);
                formik.setFieldValue('dataType', getDataType(get(selectedSensor, 'deviceObjectId', '')), false);
                formik.setFieldValue('displayDataType', get(selectedSensor, 'displayDataType', ''), false);

                const rawUnitId = get(selectedSensor, 'rawUnitId', '');
                const displayUnitId = get(selectedSensor, 'presentationUnitId', rawUnitId);

                if (rawUnitId.length) {
                    formik.setFieldValue('unit', getUnit(rawUnitId), false);
                }

                if (displayUnitId.length) {
                    formik.setFieldValue('displayUnit', getUnit(displayUnitId), false);
                }

                if (get(selectedSensor, 'displayDataType', '') !== 'float') {
                    setUnitTypeDisabled(true);
                } else {
                    formik.setFieldValue('unit', getUnit(selectedSensor.rawUnitId) || '', false);
                }

                //Check if sensor is a calculated sensor
                const calculationId = get(selectedSensor, 'sensorCalculationId', '');
                const isCalculation = get(selectedSensor, 'calculatedSensor', false);
                if (isCalculation) {
                    setUseCalc(true);
                    setCalculationId(calculationId);
                }

            }
            setProgressBarLoading(false);
        };

        if (systemId && !isEmpty(activeNavSite)) init();
    }, [activeNavSite]);

    useEffect(() => {
        const init = async () => {
            if (!isEmpty(activeNavEquipment)) {

                formik.setFieldValue('client', get(activeNavClient, 'id', ''), false);
                formik.setFieldValue('site', get(activeNavSite, 'id', ''), false);
                formik.setFieldValue('parent', get(activeNavEquipment, 'id'), false);

                if (sensorId) {
                    setProgressBarLoading(true);
                    const agentId = get(selectedSensor, 'agentId', '');
                    const deviceId = get(selectedSensor, 'deviceId', '');
                    const deviceObjectId = get(selectedSensor, 'deviceObjectId', '');

                    if (agentId !== EMPTY_GUID) {
                        const selectedAgent = await fetchAgentById(get(selectedSensor, 'agentId', ''));
                        setActiveAgent(selectedAgent);
                        const configurationId = get(selectedAgent, 'driverConfiguration[0].id', '');
                        await fetchDevicesList(get(selectedSensor, 'agentId', ''), configurationId);
                        await fetchDeviceObjects(get(selectedSensor, 'deviceId', ''));
                    }

                    formik.setFieldValue('name', get(selectedSensor, 'name', ''), false);

                    formik.setFieldValue('sensorType', get(selectedSensor, 'sensorType', ''), false);

                    if (agentId !== EMPTY_GUID) {
                        formik.setFieldValue('agent', get(selectedSensor, 'agentId', ''), false);
                    }
                    if (deviceId !== EMPTY_GUID) {
                        formik.setFieldValue('device', get(selectedSensor, 'deviceId', ''), false);
                    }
                    if (deviceObjectId !== EMPTY_GUID) {
                        formik.setFieldValue('deviceObject', get(selectedSensor, 'deviceObjectId', ''), false);
                    }


                    const rawUnitId = get(selectedSensor, 'rawUnitId', '');
                    const displayUnitId = get(selectedSensor, 'presentationUnitId', rawUnitId);
                    const conversionId = get(selectedSensor, 'conversionId', '');

                    if (conversionId) formik.setFieldValue('conversion', conversionId, false);

                    if (rawUnitId && rawUnitId.length) {
                        const selectedUnit = getUnit(rawUnitId);
                        formik.setFieldValue('unit', selectedUnit, false);

                        let displayUnits;

                        units.forEach(group => {
                            if (group.name === selectedUnit.unitType) {
                                displayUnits = [group];
                                return;
                            }
                        });

                        setDisplayUnits(displayUnits);
                    }

                    if (displayUnitId && displayUnitId.length) {
                        formik.setFieldValue('displayUnit', getUnit(displayUnitId), false);
                    }

                    formik.setFieldValue('displayDataType', get(selectedSensor, 'displayDataType', ''), false);

                    if (get(selectedSensor, 'displayDataType', '') !== 'float') {
                        setDisplayUnitTypeDisabled(true);
                    } else {
                        formik.setFieldValue('unit', getUnit(rawUnitId) || '', false);
                    }

                    //Check if sensor is a calculated sensor
                    const calculationId = get(selectedSensor, 'sensorCalculationId', '');
                    const isCalculation = get(selectedSensor, 'calculatedSensor', false);
                    if (isCalculation) {
                        setUseCalc(true);
                        setCalculationId(calculationId);
                        formik.setFieldValue('dataType', get(selectedSensor, 'displayDataType', ''), false);
                    }

                    setProgressBarLoading(false);
                }
                else {
                    await setDefaultAgentAndDevice();
                }
            }
        };

        if (!isEmpty(activeNavEquipment)) init();
    }, [activeNavEquipment]);

    useEffect(() => {
        const calculationId = get(selectedSensor, 'sensorCalculationId', '');
        const selectedCalc = sensorCalculations.find(item => item.id === calculationId);

        if (selectedCalc) {
            setSelectedCalculation(selectedCalc);
        }
    }, [activeNavEquipment, sensorCalculations, activeNavSite]);

    useEffect(() => {
        if (!isEmpty(deviceObjects) && sensorId) {
            formik.setFieldValue('dataType', getDataType(get(selectedSensor, 'deviceObjectId', '')), false);
            const calculationId = get(selectedSensor, 'sensorCalculationId', '');
            if (calculationId) {
                formik.setFieldValue('dataType', get(selectedSensor, 'displayDataType', ''), false);
            }
            if (getDataType(get(selectedSensor, 'deviceObjectId', '')) !== 'float') {
                setUnitTypeDisabled(true);
            }

            if (sensorId) setFilteredConversions(getConversionList(getDataType(get(selectedSensor, 'deviceObjectId', ''))));
        }
    }, [deviceObjects]);

    useEffect(() => {
        if (!isEmpty(conversions)) {
            const deviceObjectId = get(selectedSensor, 'deviceObjectId', '');
            setFilteredConversions(refineConversions(deviceObjectId));
        }
    }, [conversions]);

    const populateFilteredAgentList = () => {
        if (activeNavSite && activeNavSite.id) {
            const siteNotNull = cloneDeep(agents.filter(a => a.site !== null));
            const list = siteNotNull.filter(s => {
                if (s.site?.find(site => site.id === activeNavSite.id)) {
                    return true;
                }
                else {
                    return false;
                }
            });
            setFilteredAgentList(list);
        }
        else {
            setFilteredAgentList([]);
        }
    };

    const setDefaultAgentAndDevice = async () => {
        if (distinctAgentList.length === 1 && distinctAgentList[0] !== EMPTY_GUID) {

            // make sure this agent is in the list of agents
            const agentId = distinctAgentList[0];
            const foundAgent = filteredAgentList.find(agent => agent.id === agentId);
            if (foundAgent) {
                formik.setFieldValue('agent', agentId, false);
                setActiveAgent(foundAgent);
                // load the device list
                const configurationId = get(foundAgent, 'driverConfiguration[0].id', '');
                setLoadingDevices(true);
                await fetchDevicesList(agentId, configurationId);
                setLoadingDevices(false);
            }
        }
    };

    const refineConversions = deviceObjectId => {
        const dataType = getDataType(deviceObjectId);
        return conversions.filter(conversion => conversion.rawDataType === dataType);
    };

    const getDataType = deviceObjectId => {
        const deviceObject = find(deviceObjects, item => item.id === deviceObjectId);
        return get(deviceObject, 'dataType', '').toLowerCase();
    };

    const getConversionList = dataType => {
        return conversions.filter(conversion => conversion.rawDataType === dataType);
    };

    const getUnit = unitId => {
        let targetUnit;

        units.forEach(unit => {
            unit.items.forEach(item => {
                if (item.id === unitId) {
                    targetUnit = { ...item };
                }
            });
        });

        return targetUnit;
    };

    const getSensorType = sensorTypeId => {
        let targetType = '';

        sensorTypes.forEach(type => {
            type.items.forEach(item => {
                if (item.id === sensorTypeId) {
                    targetType = { ...item };
                }
            });
        });
        return targetType;
    };

    const handleAgentChange = async event => {
        setLoadingDevices(true);
        const { value } = event.target;

        formik.setFieldValue('device', '', false);
        formik.setFieldValue('deviceObject', '', false);

        formik.setTouched({
            device:       false,
            deviceObject: false
        });

        formik.handleChange(event);

        setProgressBarLoading(true);

        const selectedAgent = filteredAgentList.find(item => item.id === value);
        setActiveAgent(selectedAgent);
        const configurationId = get(selectedAgent, 'driverConfiguration[0].id', '');

        await fetchDevicesList(value, configurationId);
        setProgressBarLoading(false);
        setLoadingDevices(false);
    };

    const handleDeviceChange = async event => {
        setLoadingDeviceObjects(true);
        const { value } = event.target;

        formik.setFieldValue('deviceObject', '', false);

        formik.setTouched({
            deviceObject: false
        });

        formik.handleChange(event);
        setProgressBarLoading(true);
        await fetchDeviceObjects(value);
        setProgressBarLoading(false);
        setLoadingDeviceObjects(false);
    };

    const handleDeviceObjectChange = event => {
        const { value } = event.target;

        const deviceObject = find(deviceObjects, item => item.id === value);
        const dataType = get(deviceObject, 'dataType', '');
        setFilteredConversions(refineConversions(value));

        formik.setFieldValue('dataType', dataType, false);
        formik.setFieldValue('conversion', '', false);
        formik.setFieldValue('displayDataType', dataType, false);

        if (dataType !== 'float') {
            setUnitTypeDisabled(true);
            setDisplayUnitTypeDisabled(true);

            formik.setFieldValue('unit', '', false);
        } else {
            setUnitTypeDisabled(false);
        }

        formik.handleChange(event);
    };

    const setCalculation = calculation => {
        formik.setFieldValue('dataType', calculation.outputDataType, false);
        formik.setFieldValue('displayDataType', calculation.outputDataType, false);
        formik.setFieldValue('unit', getUnit(calculation.rawUnitId), false);
        formik.setFieldValue('displayUnit', getUnit(calculation.rawUnitId), false);
        formik.setFieldValue('sensorType', getSensorType(calculation.sensorTypeId), false);
        if (isEmpty(formik.values.name)) {
            formik.setFieldValue('name', calculation.name, false);
        }
        setCalculationId(calculation.id);

        /* Leaves time for the form to populate before manually validating
        to get rid of formik errors when selecting calculations from the list */
        setTimeout(manualValidate, 100);
    };

    const manualValidate = () => {
        formik.validateForm();
    };

    const getStringFromHTMLFormula = () => {
        let text = '';
        let sensors = [];
        let input = document.querySelector('#formula-editor');
        if (input && input.childNodes) {
            for (let i = 0; i < input.childNodes.length; i++) {
                if (input.childNodes[i].getAttribute && input.childNodes[i].getAttribute('data-ui-variable')) {
                    text += 'sensors["'+input.childNodes[i].getAttribute('data-ui-variable')+'"]';
                    sensors.push({
                        id:               input.childNodes[i].getAttribute('data-ui-variable'),
                        name:             input.childNodes[i].textContent.replaceAll('*',''),
                        isExternalSensor: input.childNodes[i].getAttribute('data-external') === 'true' ?true:false
                    });
                }
                else if (input.childNodes[i].tagName && input.childNodes[i].tagName.toLowerCase() === 'input') {
                    text += '{'+input.childNodes[i].value+'}';
                    //text += input.childNodes[i].value;
                }
                else text += input.childNodes[i].textContent;
            }
            text = text.replace(/(\r\n|\n|\r)/gm, '').trim();
            //text = text.replaceAll('*','');
            return { text: 'return ' + text, sensors: sensors };
        }
    };

    const formik = useFormik({
        initialValues:      initialValues,
        validationSchema:   SensorValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                createSensor,
                companyInfo,
                updateSensor
            } = props;
            setIsLoading(true);

            const body = {
                name:                values.name,
                sensorTypeId:        values.sensorType.id,
                rawUnitId:           values.unit.id,
                presentationUnitId:  values.displayUnit.id,
                equipmentId:         '',
                systemId:            '',
                deviceObjectId:      values.deviceObject,
                calculatedSensorIds: null,
                conversionId:        values.conversion === 'No Conversion' ? '' : values.conversion
            };

            if (useCalc) {
                let formula = getStringFromHTMLFormula();
                body.calculatedSensor = true;
                body.sensorCalculation = formula.text;

                if (sensorId) {
                    body.referencedSensorIds = formula.sensors.map(c => c.id);
                } else {
                    body.calculatedSensorIds = formula.sensors.map(c => c.id);
                }

                if (calculationId) {
                    body.sensorCalculationId = calculationId;
                } else {
                    body.sensorCalculationId = null;
                }
                //body.referencedSensorIds = null;
                //body.calculatedSensorIds = sensorSelection.map(sensor => sensor.id);
                body.runtimeHours = Number(startValue);

                if (!isEmpty(body.calculatedSensorIds)) {
                    body.calculatedSensorIds = body.calculatedSensorIds.filter(c => c);
                }
            }

            let parentId;

            if (equipmentId) {
                parentId = equipmentId;
                body.equipmentId = equipmentId;
            } else if (systemId) {
                parentId = systemId;
                body.systemId = systemId;
            }

            const handleSubmit = async () => {
                setBobLoading('SensorLoading');
                if (sensorId) {
                    let isUpdated;
                    isUpdated = await updateSensor(sensorId, parentId, siteId, companyInfo, body);

                    if (isUpdated) {
                        handleCancel();
                    }
                } else {
                    let isCreated;
                    isCreated = await createSensor(companyInfo, siteId, parentId, body);

                    if (isCreated) {
                        setStartValue(0);
                        handleCancel();
                    }
                }
                setSubmitting(false);
                setBobLoading('SensorLoading');
                setIsLoading(false);
            };

            if (activeAgent.sensorCount >= 4000) {
                setWarningModalOpen(true);
                setWarningFunction(() => () => handleSubmit());
                return;
            }

            try {
                handleSubmit();
            } catch (error) {
                setSubmitting(false);
                setIsLoading(false);
                setBobLoading('SensorLoading');
            }
        }
    });

    const handleFormReset = () => {
        formik.resetForm();
        setIsLoading(false);
    };

    const setUseCalculation = () => {
        if (!useCalc === false) {
            setCalculationId('');
            formik.setFieldValue('agent', '', false);
            formik.setFieldValue('device', '', false);
            formik.setFieldValue('deviceObject', '', false);
        }

        formik.setFieldValue('sensorType', '', false);
        formik.setFieldValue('unit', '', false);
        formik.setFieldValue('dataType', '', false);
        formik.setFieldValue('name', '', false);
        formik.setFieldValue('useCalculation', !useCalc, false);
        setUseCalc(!useCalc);
    };

    const handleCancel = () => {
        const params = new URLSearchParams(history.location.search);

        params.delete('sensor');

        let entityId = '';
        let sensors = [];
        let rule = {};

        if (sensorHistory) {
            sensors = sensorHistory.sensorHistory;
            rule = sensorHistory.ruleHistory;
            entityId = sensorHistory.entityId;
        }

        const updatedUrl = history.location.search.replace(/[?&]sensor=[^&]+/, '').replace(/^&/, '?');

        if (systemId) {
            history.push({ pathname: '/v2/dashboard/system', search: updatedUrl, state: { entityId: entityId, sensorHistory: [...sensors], ruleHistory: rule, callingLocation: history.location } });
        } else {
            history.push({ pathname: '/v2/dashboard/equipment', search: updatedUrl, state: { entityId: entityId, sensorHistory: [...sensors], ruleHistory: rule, callingLocation: history.location } });
        }
    };

    const handleConversionChange = event => {
        const { value } = event.target;
        let targetConversion;

        const conversion = find(conversions, item => item.id === value);

        if (value === 'No Conversion' || !value.length) {

            targetConversion = getDataType(get(selectedSensor, 'deviceObjectId', ''));
        } else {
            targetConversion = get(conversion, 'displayDataType', '');
        }

        if (value === 'No Conversion' || !value.length || get(conversion, 'displayDataType', '') === 'float') {

            const rawUnitId = get(selectedSensor, 'rawUnitId', '');
            const displayUnitId = get(selectedSensor, 'presentationUnitId', rawUnitId);

            if (!isNil(rawUnitId) && rawUnitId.length) {
                formik.setFieldValue('rawUnit', getUnit(rawUnitId), false);
            }
            else {
                formik.setFieldValue('rawUnit', null);
            }

            if (!isNil(displayUnitId) && displayUnitId.length) {
                formik.setFieldValue('displayUnit', getUnit(displayUnitId) || '', false);
            }
            else {
                formik.setFieldValue('displayUnit', '', false);
            }

            setDisplayUnitTypeDisabled(false);
        } else {
            setDisplayUnitTypeDisabled(true);
        }

        if (targetConversion === 'float') {
            setDisplayUnitTypeDisabled(false);
        } else {
            formik.setFieldValue('rawUnit', '', false);
            formik.setFieldValue('displayUnit', '', false);
        }

        formik.setFieldValue('displayDataType', targetConversion || formik.values.dataType, false);

        setIsLoading(false);

        formik.handleChange(event);
    };

    const handleUnitChange = event => {
        formik.handleChange(event);
        let displayUnits;

        units.forEach(group => {
            if (group.name === event.target.value.unitType) {
                displayUnits = [group];
                return;
            }
        });

        setDisplayUnits(displayUnits);

        if (!formik.values.displayUnit) {
            formik.setFieldValue('displayUnit', event.target.value);
        } else if (formik.values.displayUnit.unitType !== event.target.value.unitType) {
            formik.setFieldValue('displayUnit', '');
        }
    };

    const handleDisplayUnitChange = event => {
        formik.handleChange(event);
    };

    const startValueChange = e => {
        setStartValue(e.target.value);
    };

    const handleChangeDataType = e => {
        formik.setFieldValue('displayDataType', e.target.value);
        formik.setFieldValue('dataType', e.target.value);
    };

    const handleCalculationSelect = item => {
        setSensorSelection(['']);

        setCalculation(item);
        setSelectedCalculation(item);
    };

    const getSensorIds = () => {
        let sensors = new Map();
        let input = document.querySelector('#formula-editor');
        if (input && input.childNodes) {
            for (let i = 0; i < input.childNodes.length; i++) {
                if (input.childNodes[i].getAttribute && input.childNodes[i].getAttribute('data-ui-variable')) {
                    sensors.set(input.childNodes[i].getAttribute('data-ui-variable'), i + 1);
                }
            }
        }
        return sensors;
    };

    const handleTestConfirmation = () => {
        const init = async (sensors) => {
            let promises = [];
            let sensorTypeIds = Array.from(sensors.keys());
            let _sensors = [];
            for (let sid of sensorTypeIds) {
                let result = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(sid);

                if (!result) {
                    setCalculatedVal('Expression is not valid.');
                    dispatch(enqueueSnackbar('One or more sensors not selected.', 'error'));
                    setIsValidFormula(false);
                    return;
                }

                if (activeNavEquipment.extendedData && activeNavEquipment.extendedData.sensors) {
                    let ts = activeNavEquipment.extendedData.sensors.filter(c => c.id === sid);
                    if (ts && ts.length > 0) {
                        _sensors.push(ts[0].id);
                    }
                    else {
                        if (!isEmpty(externalSensors) && externalSensors.filter(c => c.id === sid)) {
                            let ts1 = externalSensors.filter(c => c.id === sid);
                            if (ts1 && ts1.length > 0) {
                                _sensors.push(ts1[0].id);
                            }
                        } else {
                            const fetchedSensor = await fetchTemplateSensorById(sid);
                            if (fetchedSensor) {
                                const updatedExternalSensors = [...externalSensors];
                                updatedExternalSensors.push(fetchedSensor);
                                setExternalSensors(updatedExternalSensors);
                                _sensors.push(fetchedSensor.id);
                            }
                        }
                    }
                }
            }

            promises.push(fetchEntitySensorData(_sensors.map(c => {
                return { 'id': c };
            }), null, null, 60));
            await Promise.all(promises.map(p => p.catch(err => err)));
        };

        let sensors = getSensorIds();

        if (sensors.size > 0) {
            let template = getStringFromHTMLFormula();
            template = template.text.replace(/\u00A0/g, '');

            let templates = template.split('=');
            for (let i = 0; i < templates.length; i++) {
                let tempFormula = templates[i].replaceAll('avg', 'mean');

                const constRegexs = /\{[^}]*\}/g;
                const constantsFound = tempFormula.match(constRegexs);

                if (constantsFound) {
                    const empty = constantsFound.find(item => item === '{}');
                    if (empty) {
                        setCalculatedVal('Expression is not valid.');
                        dispatch(enqueueSnackbar('Expression is not valid.', 'error'));
                        setIsValidFormula(false);
                        return;
                    }
                }
            }

            let sensors = getSensorIds();
            init(sensors);
        } else {
            let scope = {
                A: 1,
                B: 2,
                C: 3,
                D: 2,
                E: 4,
                F: 5,
                G: 6,
                H: 7,
                I: 8,
                J: 9,
                K: 10,
                L: 11,
                M: 12,
                N: 13,
                O: 14,
                P: 15,
                Q: 16,
                R: 17,
                S: 18,
                T: 19,
                U: 20,
                V: 21,
                W: 22,
                X: 23,
                Y: 24,
                Z: 25
            };

            for (let key of Object.keys(scope)) {
                scope[key.toLocaleLowerCase()] = scope[key];
            }

            let template = getStringFromHTMLFormula();

            if (!template || template.text.trim() === 'return') {
                setCalculatedVal('Expression is not valid.');
                dispatch(enqueueSnackbar('Expression is not valid.', 'error'));
                return;
            }
            template = template.text.replace(/\u00A0/g, '');

            try {
                let templates = template.split('=');
                for (let i = 0; i < templates.length; i++) {
                    let tempFormula = templates[i].replaceAll('avg', 'mean');

                    const constRegexs = /\{[^}]*\}/g;
                    const constantsFound = tempFormula.match(constRegexs);

                    if (constantsFound) {
                        const empty = constantsFound.find(item => item === '{}');
                        if (empty) {
                            setCalculatedVal('Expression is not valid.');
                            dispatch(enqueueSnackbar('Expression is not valid.', 'error'));
                            setIsValidFormula(false);
                            return;
                        }
                    }

                    tempFormula = tempFormula.replaceAll('{', '');
                    tempFormula = tempFormula.replaceAll('}', '');
                    tempFormula = tempFormula.replaceAll('return', '');

                    if (tempFormula.trim() === '') {
                        dispatch(enqueueSnackbar('Expression is not valid.', 'error'));
                        return;
                    }

                    tempFormula = tempFormula.replaceAll('Math.', '');

                    let result = math.evaluate(tempFormula, scope);
                    setCalculatedVal(tempFormula + ' = ' + result);

                }
                dispatch(enqueueSnackbar('Calculation tested successfully.', 'success'));
                setIsValidFormula(true);
            } catch (e) {
                setIsValidFormula(false);
                setCalculatedVal('Expression is not valid.');
                dispatch(enqueueSnackbar('Expression is not valid.', 'error'));
            }
        }
    };

    const setExternalEquipment = eq => {
        extEq.current = eq;
    };

    return (
        <div className={classes.SensorForm}>
            <div className={classes.FormContainer}>
                <div>
                    <FormControl className={classes.InputWrapper}>
                        <InputLabel
                            htmlFor="client"
                            classes={{
                                error:   classes.Error,
                                focused: classes.LabelFocused,
                                root:    classes.Label
                            }}
                            error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                        >
                            Client<span className={classes.Required}> *</span>
                        </InputLabel>
                        <Select
                            value={formik.values.client}
                            onChange={formik.handleChange}
                            className={classes.Select}
                            disabled={true}
                            inputProps={{
                                name:   'client',
                                id:     'client',
                                onBlur: formik.handleBlur
                            }}
                            error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                        >
                            <MenuItem
                                key={activeNavClient.id}
                                value={activeNavClient.id}
                            >
                                {activeNavClient.name}
                            </MenuItem>
                        </Select>
                        {formik.touched.client &&
                            formik.errors.client && <FormHelperText className={classes.Error}>{formik.errors.client}</FormHelperText>}
                    </FormControl>
                </div>
                <div>
                    <FormControl className={classes.InputWrapper}>
                        <InputLabel
                            htmlFor="site"
                            classes={{
                                error:   classes.Error,
                                focused: classes.LabelFocused,
                                root:    classes.Label
                            }}
                            error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                        >
                            Site<span className={classes.Required}> *</span>
                        </InputLabel>
                        <Select
                            value={formik.values.site}
                            onChange={formik.handleChange}
                            className={classes.Select}
                            disabled={true}
                            inputProps={{
                                name:   'site',
                                id:     'site',
                                onBlur: formik.handleBlur
                            }}
                            error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                        >
                            <MenuItem
                                key={activeNavSite.id}
                                value={activeNavSite.id}
                            >
                                {activeNavSite.name}
                            </MenuItem>
                        </Select>
                        {formik.touched.site &&
                            formik.errors.site && <FormHelperText className={classes.Error}>{formik.errors.site}</FormHelperText>}
                    </FormControl>
                </div>
                <div>
                    {
                        !systemId ?
                            <FormControl className={classes.InputWrapper}>
                                <InputLabel
                                    htmlFor="parent"
                                    classes={{
                                        error:   classes.Error,
                                        focused: classes.LabelFocused,
                                        root:    classes.Label
                                    }}
                                    error={Boolean(formik.errors.parent) && Boolean(formik.touched.parent)}
                                >
                                    Equipment<span className={classes.Required}> *</span>
                                </InputLabel>
                                <Select
                                    value={formik.values.parent}
                                    onChange={formik.handleChange}
                                    className={classes.Select}
                                    disabled={true}
                                    inputProps={{
                                        name:   'parent',
                                        id:     'parent',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.parent) && Boolean(formik.touched.parent)}
                                >
                                    <MenuItem
                                        key={activeNavEquipment.id}
                                        value={activeNavEquipment.id}
                                    >
                                        {activeNavEquipment.name}
                                    </MenuItem>
                                </Select>
                                {formik.touched.parent &&
                                    formik.errors.parent && <FormHelperText className={classes.Error}>{formik.errors.parent}</FormHelperText>}
                            </FormControl>
                            :
                            <FormControl className={classes.InputWrapper}>
                                <InputLabel
                                    htmlFor="parent"
                                    classes={{
                                        error:   classes.Error,
                                        focused: classes.LabelFocused,
                                        root:    classes.Label
                                    }}
                                    error={Boolean(formik.errors.parent) && Boolean(formik.touched.parent)}
                                >
                                    System<span className={classes.Required}> *</span>
                                </InputLabel>
                                <Select
                                    value={formik.values.parent}
                                    onChange={formik.handleChange}
                                    className={classes.Select}
                                    disabled={true}
                                    inputProps={{
                                        name:   'parent',
                                        id:     'parent',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.parent) && Boolean(formik.touched.parent)}
                                >
                                    {
                                        systems ?
                                            systems.map(system =>
                                                <MenuItem
                                                    key={system.id}
                                                    value={system.id}
                                                >
                                                    {system.name}
                                                </MenuItem>
                                            )
                                            :
                                            null
                                    }
                                </Select>
                                {formik.touched.parent &&
                                    formik.errors.parent && <FormHelperText className={classes.Error}>{formik.errors.parent}</FormHelperText>}
                            </FormControl>
                    }
                </div>
                <div>
                    <FormControl className={classes.InputWrapper}>
                        <TextField
                            id="name"
                            name="name"
                            type="text"
                            disabled={false}
                            value={formik.values.name}
                            label={<span>Sensor Name<span className={classes.Required}> *</span></span>}
                            onChange={formik.handleChange}
                            InputLabelProps={{
                                classes: {
                                    error:   classes.Error,
                                    focused: classes.LabelFocused,
                                    root:    classes.Label
                                }
                            }}
                            InputProps={{
                                onBlur:  formik.handleBlur,
                                classes: {
                                    error:     classes.CssErrorUnderline,
                                    underline: classes.CssUnderline,
                                    root:      classes.Input
                                }
                            }}
                            FormHelperTextProps={{
                                classes: {
                                    error: classes.Error
                                }
                            }}
                            error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                            helperText={Boolean(formik.touched.name) && formik.errors.name}
                        />
                    </FormControl>
                </div>
                <div>
                    <FormControl className={classes.AutocompleteWrapper}>
                        <AutoComplete
                            grouped
                            overrideClass={classes.AutocompleteField}
                            overrideErrorClass={classes.AutocompleteError}
                            overrideLabelClass={classes.AutocompleteLabel}
                            label={<React.Fragment><span>Sensor Type</span><span className={classes.Required}> *</span></React.Fragment>}
                            name="sensorType"
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            options={sensorTypes}
                            placeholder="Sensor Type"
                            value={formik.values.sensorType}
                            error={Boolean(formik.errors.sensorType) && Boolean(formik.touched.sensorType)}
                            helperText={Boolean(formik.touched.sensorType) && formik.errors.sensorType}
                        />
                    </FormControl>
                </div>
                <div>
                    <FormControl className={classes.InputWrapper}>
                        {
                            useCalc ?
                                <React.Fragment>
                                    <InputLabel
                                        htmlFor="dataType"
                                        classes={{
                                            error:   classes.Error,
                                            focused: classes.LabelFocused,
                                            root:    classes.Label
                                        }}
                                        error={Boolean(formik.errors.dataType) && Boolean(formik.touched.dataType)}
                                    >
                                        Data Type<span className={classes.Required}> *</span>
                                    </InputLabel>
                                    <Select
                                        id="dataType"
                                        name="dataType"
                                        value={formik.values.dataType}
                                        onChange={handleChangeDataType}
                                        className={classes.Select}
                                        disabled={false}
                                        inputProps={{
                                            name:   'dataType',
                                            id:     'dataType',
                                            onBlur: formik.handleBlur
                                        }}
                                        error={Boolean(formik.errors.dataType) && Boolean(formik.touched.dataType)}
                                    >
                                        {
                                            dataTypes.map((type, index) => {
                                                return (
                                                    <MenuItem
                                                        key={index}
                                                        value={type}
                                                    >
                                                        {startCase(type)}
                                                    </MenuItem>
                                                );
                                            })
                                        }
                                    </Select>
                                </React.Fragment>
                                :
                                <TextField
                                    disabled
                                    id="dataType"
                                    name="dataType"
                                    type="text"
                                    value={formik.values.dataType}
                                    label="Data Type"
                                    onChange={formik.handleChange}
                                    InputLabelProps={{
                                        classes: {
                                            error:   classes.error,
                                            focused: classes.labelFocused,
                                            root:    classes.label
                                        }
                                    }}
                                    InputProps={{
                                        onBlur:  formik.handleBlur,
                                        classes: {
                                            error:     classes.cssErrorUnderline,
                                            underline: classes.cssUnderline,
                                            root:      classes.input
                                        }
                                    }}
                                    FormHelperTextProps={{
                                        classes: {
                                            error: classes.error
                                        }
                                    }}
                                    error={Boolean(formik.errors.dataType) && Boolean(formik.touched.dataType)}
                                    helperText={Boolean(formik.touched.dataType) && formik.errors.dataType}
                                />
                        }
                    </FormControl>
                </div>
                <div>
                    <FormControl className={classes.InputWrapper}>
                        {
                            useCalc ?
                                <React.Fragment>
                                    <InputLabel
                                        htmlFor="displayDataType"
                                        classes={{
                                            error:   classes.Error,
                                            focused: classes.LabelFocused,
                                            root:    classes.Label
                                        }}
                                        error={Boolean(formik.errors.displayDataType) && Boolean(formik.touched.displayDataType)}
                                    >
                                        Display Data Type<span className={classes.Required}> *</span>
                                    </InputLabel>
                                    <Select
                                        id="displayDataType"
                                        name="displayDataType"
                                        value={formik.values.displayDataType}
                                        onChange={handleChangeDataType}
                                        className={classes.Select}
                                        disabled={false}
                                        inputProps={{
                                            name:   'displayDataType',
                                            id:     'displayDataType',
                                            onBlur: formik.handleBlur
                                        }}
                                        error={Boolean(formik.errors.displayDataType) && Boolean(formik.touched.displayDataType)}
                                    >
                                        {
                                            dataTypes.map((type, index) => {
                                                return (
                                                    <MenuItem
                                                        key={index}
                                                        value={type}
                                                    >
                                                        {startCase(type)}
                                                    </MenuItem>
                                                );
                                            })
                                        }
                                    </Select>
                                </React.Fragment>
                                :
                                <TextField
                                    disabled
                                    id="displayDataType"
                                    name="displayDataType"
                                    type="text"
                                    value={formik.values.displayDataType}
                                    label="Display Data Type"
                                    onChange={formik.handleChange}
                                    InputLabelProps={{
                                        classes: {
                                            error:   classes.error,
                                            focused: classes.labelFocused,
                                            root:    classes.label
                                        }
                                    }}
                                    InputProps={{
                                        onBlur:  formik.handleBlur,
                                        classes: {
                                            error:     classes.cssErrorUnderline,
                                            underline: classes.cssUnderline,
                                            root:      classes.input
                                        }
                                    }}
                                    FormHelperTextProps={{
                                        classes: {
                                            error: classes.error
                                        }
                                    }}
                                    error={Boolean(formik.errors.displayDataType) && Boolean(formik.touched.displayDataType)}
                                    helperText={Boolean(formik.touched.displayDataType) && formik.errors.displayDataType}
                                />
                        }
                    </FormControl>
                </div>
                {
                    !useCalc ?
                        <div>
                            <FormControl className={classes.InputWrapper}>
                                <InputLabel
                                    htmlFor="agent"
                                    classes={{
                                        error:   classes.Error,
                                        focused: classes.LabelFocused,
                                        root:    classes.Label
                                    }}
                                    error={Boolean(formik.errors.agent) && Boolean(formik.touched.agent)}
                                >
                                    Agent<span className={classes.Required}> *</span>
                                </InputLabel>
                                <Select
                                    value={formik.values.agent}
                                    onChange={handleAgentChange}
                                    className={classes.Select}
                                    disabled={false}
                                    inputProps={{
                                        name:   'agent',
                                        id:     'agent',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.agent) && Boolean(formik.touched.agent)}
                                >
                                    {
                                        orderBy(filteredAgentList, [agent => agent.name.toLowerCase()], ['asc']).map(agent =>
                                            <MenuItem
                                                key={agent.id}
                                                value={agent.id}
                                            >
                                                {agent.name}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {
                                    activeAgent && activeAgent.sensorCount >= 4000 ?
                                        <FormHelperText className={classes.Error}>Agent contains over 4000 sensors</FormHelperText>
                                        :
                                        null
                                }
                                {formik.touched.agent &&
                                    formik.errors.agent && <FormHelperText className={classes.Error}>{formik.errors.agent}</FormHelperText>}
                            </FormControl>
                        </div>
                        :
                        null
                }
                {
                    !useCalc ?
                        <div>
                            <FormControl className={classes.InputWrapper}>
                                <InputLabel
                                    htmlFor="device"
                                    classes={{
                                        error:   classes.Error,
                                        focused: classes.LabelFocused,
                                        root:    classes.Label
                                    }}
                                    error={Boolean(formik.errors.device) && Boolean(formik.touched.device)}
                                >
                                    Device<span className={classes.Required}> *</span>
                                </InputLabel>
                                <Select
                                    value={formik.values.device}
                                    onChange={handleDeviceChange}
                                    className={classes.Select}
                                    disabled={!formik.values.agent || loadingDevices}
                                    endAdornment={
                                        loadingDevices ?
                                            <InputAdornment position="end">
                                                <CircularProgress className={classes.Adornment} size={14} />
                                            </InputAdornment>
                                            :
                                            null
                                    }
                                    inputProps={{
                                        name:   'device',
                                        id:     'device',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.device) && Boolean(formik.touched.device)}
                                >
                                    {
                                        isEmpty(devices) ?
                                            <MenuItem
                                                key={'start'}
                                                disabled={true}
                                                value={'none'}
                                            >
                                                Agent has no devices
                                            </MenuItem>
                                            :
                                            null
                                    }
                                    {
                                        devices.map(device =>
                                            <MenuItem
                                                key={device.id}
                                                value={device.id}
                                            >
                                                {device.name}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.device &&
                                    formik.errors.device && <FormHelperText className={classes.Error}>{formik.errors.device}</FormHelperText>}
                            </FormControl>
                        </div>
                        :
                        null
                }
                {
                    !useCalc ?
                        <div>
                            <FormControl className={classes.InputWrapper}>
                                <InputLabel
                                    htmlFor="deviceObject"
                                    classes={{
                                        error:   classes.Error,
                                        focused: classes.LabelFocused,
                                        root:    classes.Label
                                    }}
                                    error={Boolean(formik.errors.deviceObject) && Boolean(formik.touched.deviceObject)}
                                >
                                    Device Object<span className={classes.Required}> *</span>
                                </InputLabel>
                                <Select
                                    value={formik.values.deviceObject}
                                    onChange={handleDeviceObjectChange}
                                    className={classes.Select}
                                    disabled={!formik.values.device || loadingDeviceObjects}
                                    endAdornment={
                                        loadingDeviceObjects ?
                                            <InputAdornment position="end">
                                                <CircularProgress className={classes.Adornment} size={14} />
                                            </InputAdornment>
                                            :
                                            null
                                    }
                                    inputProps={{
                                        name:   'deviceObject',
                                        id:     'deviceObject',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.deviceObject) && Boolean(formik.touched.deviceObject)}
                                >
                                    {
                                        isEmpty(deviceObjects) ?
                                            <MenuItem
                                                key={'start'}
                                                disabled={true}
                                                value={'none'}
                                            >
                                                Device has no objects
                                            </MenuItem>
                                            :
                                            null
                                    }
                                    {
                                        deviceObjects.map(objectItem =>
                                            <MenuItem
                                                key={objectItem.id}
                                                value={objectItem.id}
                                            >
                                                {objectItem.name}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.deviceObject &&
                                    formik.errors.deviceObject && <FormHelperText className={classes.Error}>{formik.errors.deviceObject}</FormHelperText>}
                            </FormControl>
                        </div>
                        :
                        null
                }
                <div>
                    <FormControl className={classes.InputWrapper}>
                        <AutoComplete
                            grouped
                            disabled={unitTypeDisabled}
                            overrideClass={classes.AutocompleteField}
                            overrideErrorClass={classes.AutocompleteError}
                            overrideLabelClass={classes.AutocompleteLabel}
                            label="Raw Unit"
                            name="unit"
                            onBlur={formik.handleBlur}
                            onChange={handleUnitChange}
                            options={units}
                            placeholder="Raw Unit"
                            value={formik.values.unit}
                            error={Boolean(formik.errors.unit) && Boolean(formik.touched.unit)}
                            helperText={Boolean(formik.touched.unit) && formik.errors.unit}
                        />
                    </FormControl>
                </div>
                <div>
                    <FormControl className={classes.InputWrapper}>
                        <AutoComplete
                            grouped
                            disabled={displayUnitTypeDisabled}
                            overrideClass={classes.AutocompleteField}
                            overrideErrorClass={classes.AutocompleteError}
                            overrideLabelClass={classes.AutocompleteLabel}
                            label="Display Unit"
                            name="displayUnit"
                            onBlur={formik.handleBlur}
                            onChange={handleDisplayUnitChange}
                            options={displayUnits}
                            placeholder="Display Unit"
                            value={formik.values.displayUnit}
                            error={Boolean(formik.errors.displayUnit) && Boolean(formik.touched.displayUnit)}
                            helperText={Boolean(formik.touched.displayUnit) && formik.errors.displayUnit}
                        />
                    </FormControl>
                </div>
                {
                    !useCalc ?
                        <div>
                            <FormControl className={classes.InputWrapper}>
                                <InputLabel
                                    htmlFor="conversion"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.conversion) && Boolean(formik.touched.conversion)}
                                >
                                    Conversion
                                </InputLabel>
                                <Select
                                    name="conversion"
                                    id="conversion"
                                    value={formik.values.conversion}
                                    onChange={handleConversionChange}
                                    classes={{
                                        root: classes.select
                                    }}
                                    inputProps={{
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.conversion) && Boolean(formik.touched.conversion)}
                                >
                                    <MenuItem
                                        key={'No Conversion'}
                                        value={'No Conversion'}
                                    >
                                        No Conversion
                                    </MenuItem>
                                    {
                                        filteredConversions.map(type =>
                                            <MenuItem
                                                key={type.id}
                                                value={type.id}
                                            >
                                                {type.name}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.conversion &&
                                    formik.errors.conversion && <FormHelperText className={classes.error}>{formik.errors.conversion}</FormHelperText>}
                            </FormControl>
                        </div>
                        :
                        null
                }
                <div>
                    {
                        process.env.sensorCalculationEnabled ?
                            <FormControl className={classes.CheckboxInputWrapper}>
                                <FormControlLabel
                                    className={classes.checkboxLabel}
                                    control={
                                        <Checkbox
                                            onChange={() => setUseCalculation(!useCalc)}
                                            checked={useCalc}
                                            disabled={sensorId ? true : false}
                                            name="checkbox"
                                            value="checkbox"
                                            color="primary"
                                        />
                                    }
                                    label="Use Calculation"
                                />
                            </FormControl>
                            :
                            null
                    }
                </div>
                {
                    useCalc ?
                        <div>
                            <FormControl className={classes.InputWrapper}>
                                <TextField
                                    id="startValue"
                                    name="startValue"
                                    type="number"
                                    size="small"
                                    label={<span>Starting Value <span className={classes.Required}> *</span></span>}
                                    value={startValue}
                                    onChange={(e) => startValueChange(e)}
                                    inputProps={{
                                        min: 0
                                    }}
                                />
                            </FormControl>
                        </div>
                        :
                        null
                }
            </div>
            {
                useCalc ?
                    <div className={classes.CalculationContainer}>
                        <div className={classes.InfoPanel}>
                            <p className={classes.HeaderLabel}>K2A Calculations</p>
                            <div className={classes.SearchContainer}>
                                <span className={clsx('material-icons-outlined md-18', classes.SearchBar)}>
                                    search
                                </span>
                                <TextField
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className={classes.margin}
                                    id="input-with-icon-textfield"
                                    placeholder="Search by keyword"
                                    InputProps={{
                                        style:            { fontSize: '1.2rem' },
                                        disableUnderline: true
                                    }}
                                    InputLabelProps={{
                                        style: { color: '#c6c6c6' },
                                    }}
                                />
                            </div>
                            {isEmpty(sensorCalculations) ?
                                <div>No Available Calculations</div>
                                :
                                <div className={classes.ListContainer}>
                                    {
                                        sensorCalculations.filter(item => item.name.toLowerCase().includes(search.toLowerCase())).map((item, index) =>
                                            <div key={index} className={classes.ButtonList}>
                                                <Button
                                                    className={selectedCalculation && item.id === selectedCalculation.id ? classes.SelectedSensorButton : classes.SensorButton}
                                                    onClick={() => handleCalculationSelect(item)}
                                                >
                                                    {item.name}
                                                </Button>
                                            </div>
                                        )
                                    }
                                </div>
                            }
                        </div>
                        <div style={{ width: 'calc(100% - 200px)' }} >
                            <CustomCalculationPanel
                                sensorId={sensorId}
                                selectedCalculation={selectedCalculation}
                                sensorCalculations={sensorCalculations}
                                sensors={sensors}
                                sensorTypes={sensorTypes}
                                selectedSensor={selectedSensor}
                                setSelectedCalculation={setSelectedCalculation}
                                setCalculation={setCalculation}
                                setIsValidFormula={setIsValidFormula}
                                setSensorSelection={setSensorSelection}
                                setExternalEquipment={setExternalEquipment}
                                setExternalSensors={setExternalSensors}
                                externalSensors={externalSensors}
                            />
                            <span className={classes.calculateLbl}>{`Tested value: ${calculatedVal ? calculatedVal : '--'}`}</span>
                        </div>
                    </div>
                    :
                    null
            }
            <div className={classes.ButtonContainer}>
                <BobCancelButton
                    onClick={handleCancel}
                    className={classes.Buttons}
                    label={'Close'}
                />
                <BobConfirmButton
                    disabled={isLoading || (useCalc && !isValidFormula) ? true : false}
                    onClick={formik.handleSubmit}
                    className={isLoading || (useCalc && !isValidFormula) ? classes.DisabledButtons : classes.Buttons}
                    label={'Save'}
                />
                {
                    useCalc ?
                        <BobConfirmButton
                            className={classes.Buttons} isLoading={false}
                            onClick={handleTestConfirmation} label={'Test Calculation'}
                        />
                        :
                        null
                }
                <BobConfirmButton
                    hidden={true}
                    className={classes.Buttons}
                    label={'Validate Formula'}
                />
            </div>
            {
                warningModalOpen ?
                    <CreateSensorWarningModal
                        handleSubmit={warningFunction}
                        handleReset={handleFormReset}
                        setWarningModalOpen={setWarningModalOpen}
                    />
                    :
                    null
            }
            {
                bobLoading.includes('SensorLoading') ?
                    <BobLoadingMask />
                    :
                    null
            }
        </div>
    );
};

const mapStateToProps = ({ basAgent, ...state }) => {
    const { basAgents } = basAgent;

    return {
        activeNavEquipment: state.bob.activeNavEquipment,
        activeNavClient:    state.bob.activeNavClient,
        activeNavSite:      state.bob.activeNavSite,
        agents:             Object.values(basAgents),
        bobLoading:         state.bob.bobLoading,
        clients:            state.architect.clientNodes,
        conversions:        state.architect.conversions,
        companyInfo:        state.companyInfo,
        devices:            state.device.devices,
        deviceObjects:      state.device.deviceObjects,
        entitySensorData:   state.bob.entitySensorData,
        navClients:         state.bob.navClients,
        navEquipment:       state.bob.navEquipment,
        navSites:           state.bob.navSites,
        selectedSensor:     state.dashboard.selectedSensor,
        sensorTypes:        state.sensorTypes.data,
        units:              state.architect.rawUnits
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...BasAgentActions,
    ...BobActions,
    ...CoreActions,
    ...DashboardActions,
    ...DeviceActions,
    ...SensorTypeActions
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SensorForm));
